import { useOrganization } from "./useOrganization";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Feature gating hook — restricts OTO features for jvzoo_fe plan users,
 * and Power Pack (OTO2) features for jvzoo_fe and jvzoo_oto-only users.
 * All other plans (beta, free/trial, jvzoo_oto_oto2, paid plans, etc.) get full access.
 */
export function useSubscriptionPlan() {
  const { organization, isLoading } = useOrganization();

  const plan = organization?.subscription_plan || "free";

  // Only jvzoo_fe users are restricted from Growth Pack features
  const isJVZooFE = plan === "jvzoo_fe";

  // Power Pack features available when plan contains 'oto2', or is beta/free/trial
  const hasPowerPack = plan.includes("oto2") || plan === "beta" || plan === "free" || plan === "trial" || plan === "founders" || (!plan.startsWith("jvzoo_"));

  // Check if this is the review account (to show tier badges)
  const { data: currentUserEmail } = useQuery({
    queryKey: ["current-user-email-for-review", organization?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.email || null;
    },
    staleTime: 0, // Always refetch to handle account switches
  });

  const isReviewAccount = currentUserEmail === "review@quotesendgo.com";

  return {
    plan,
    isLoading,

    // Growth Pack feature flags — true = feature is available
    hasSMSNotifications: !isJVZooFE,
    hasAutomatedReminders: !isJVZooFE,
    hasRecurringBilling: !isJVZooFE,
    hasPaymentPlans: !isJVZooFE,
    hasTeamMembers: !isJVZooFE,
    hasFutureUpdates: !isJVZooFE,
    hasLineItemImages: !isJVZooFE,
    hasOptionalLineItems: !isJVZooFE,

    // Power Pack feature flags
    hasProBranding: hasPowerPack,
    hasReporting: hasPowerPack,
    hasTemplates: hasPowerPack,
    hasAIQuoteBuilder: hasPowerPack,
    hasAIDescriptions: hasPowerPack,

    // Team member limits (extra seats beyond the owner)
    // Growth Pack (OTO): 1 extra, Power Pack (OTO2): 3 extra, both: 4 extra
    maxTeamMembers: (() => {
      if (plan === "jvzoo_fe") return 0;
      if (plan === "beta" || plan === "free" || plan === "trial" || plan === "founders" || !plan.startsWith("jvzoo_")) return 99;
      const hasOto = plan.includes("oto") && !plan.endsWith("oto2") || plan.includes("oto_oto2");
      const hasOto2Only = plan === "jvzoo_oto2";
      if (plan === "jvzoo_oto_oto2") return 4;
      if (plan === "jvzoo_oto") return 1;
      if (hasOto2Only) return 3;
      return 0;
    })(),

    // Helper for conditional rendering
    isRestricted: isJVZooFE,

    // Review account shows tier badges
    isReviewAccount,
  };
}
